/*
Copyright (c) Uber Technologies, Inc.

This source code is licensed under the MIT license found in the
LICENSE file in the root directory of this source tree.
*/
import { elevationTokensV2CssVars } from '@uber/base-specs';

/**
 * Base 2.0 elevation hierarchy, exposed on `theme.elevation`.
 *
 * Keys are named `shadow{Hierarchy}` rather than mirroring base-specs'
 * `elevation{Hierarchy}` so consumers read `theme.elevation.shadowMid`
 * instead of the stuttering `theme.elevation.elevationMid`. This also matches
 * the design-spec terminology ("shadow") and the existing v1
 * `theme.lighting.shadow*` naming.
 *
 * Each value is a complete `box-shadow` declaration composed from the
 * underlying `--base-elevation-*` CSS custom properties, so it responds to
 * data attributes at runtime (see base-elevation-v2.css) without a JS theme
 * swap — e.g. `css({ boxShadow: theme.elevation.shadowMid })`.
 *
 * NOTE: `shadowHighest` is intended for desktop surfaces only.
 */
const elevationTokens = ({
  shadowLowest: elevationTokensV2CssVars.elevationLowest,
  shadowLow: elevationTokensV2CssVars.elevationLow,
  shadowMid: elevationTokensV2CssVars.elevationMid,
  shadowHigh: elevationTokensV2CssVars.elevationHigh,
  shadowHighest: elevationTokensV2CssVars.elevationHighest
} as const);
export type Elevation = Record<keyof typeof elevationTokens, string>;
export { elevationTokens as default };